import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, Box, Typography, Grid, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

const IAMInviteModal = ({ open, onClose, email, onSendInvite }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: ''
  });
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSendInvite = () => {
    if (validateForm()) {
      onSendInvite({
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: email
      });
      handleClose();
    }
  };

  const handleClose = () => {
    setFormData({ firstName: '', lastName: '' });
    setErrors({});
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 8px 40px rgba(0,0,0,0.12)'
        }
      }}
    >
      <DialogTitle
        sx={{
          pb: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          bgcolor: 'success.50',
          color: 'success.main',
          fontWeight: 600
        }}
      >
        Send IAM Invite
        <IconButton onClick={handleClose} size="small" sx={{ color: 'success.main' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="body1" color="textSecondary" sx={{ mb: 1 }}>
            Sending invite to:
          </Typography>
          <Typography variant="h6" color="primary.main" sx={{ fontWeight: 500 }}>
            {email}
          </Typography>
        </Box>

        <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
          Please provide the user&apos;s name to complete the IAM invitation.
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextField
              fullWidth
              name="firstName"
              label="First Name"
              placeholder="Enter first name"
              value={formData.firstName}
              onChange={handleChange}
              error={!!errors.firstName}
              helperText={errors.firstName || ''}
              required
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'success.main'
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'success.main'
                  }
                }
              }}
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              fullWidth
              name="lastName"
              label="Last Name"
              placeholder="Enter last name"
              value={formData.lastName}
              onChange={handleChange}
              error={!!errors.lastName}
              helperText={errors.lastName || ''}
              required
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'success.main'
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'success.main'
                  }
                }
              }}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          color="inherit"
          sx={{
            borderRadius: 2,
            px: 3
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSendInvite}
          variant="contained"
          color="success"
          disabled={!formData.firstName.trim() || !formData.lastName.trim()}
          sx={{
            borderRadius: 2,
            px: 3,
            ml: 2
          }}
        >
          Send IAM Invite
        </Button>
      </DialogActions>
    </Dialog>
  );
};

IAMInviteModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  email: PropTypes.string.isRequired,
  onSendInvite: PropTypes.func.isRequired
};

export default IAMInviteModal;
